import React, { useState, forwardRef, useRef } from "react";
import {
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { StyledTextField } from "../components/formStyles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "../../style/form.css";

const FormInput = forwardRef(
  (
    {
      options = [],
      disabled = false,
      fullWidth = false,
      label = "",
      placeholder = " ",
      size = "small",
      type = "text",
      maxRows,
      className,
      spacing,
      inputProps = {},
      backgroundColor,
      hideCountButton = false,
      invertBorder = false,
      error = {},
      errorTextClassName = "",
      hidden = false,
      control = false,
      name = "test",
      rules = {},
      readOnly = false,
      style,
      multipleLine = false,
      required = false,
      helperText,
      onClick,
      onFocus,
      defaultValue,
      handleCopy,
      handlePast,
      handleCut,
      autocomplete,
      contentEditable,
      classNameFormGrid,

      formType = "defaultForm",
      ...props
    },
    ref
  ) => {
    const { control: childControl } = useForm();
    const [showPassword, setShowPassword] = useState(false);

    const inputRef = useRef(null);

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleIconClick = () => {
      if (inputRef.current && inputRef.current.type === "date") {
        inputRef.current.showPicker();
      }
    };

    return (
      <Grid
        className={`form-input-container ${
          hidden ? "hidden" : ""
        } ${classNameFormGrid}`}
        container
        flexDirection="column"
        spacing={spacing}
      >
        <Grid item>
          <Controller
            name={name}
            control={control || childControl}
            rules={rules}
            defaultValue={defaultValue}
            render={({ field: { value, onChange, onBlur } }) =>
              type === "select" ? (
                <FormControl fullWidth>
                  <InputLabel
                    id={`select-label-${name}`}
                    sx={{
                      color: "#000000ce",
                      "&.Mui-focused": {
                        color: "#333",
                      },
                    }}
                  >
                    {`${label}${required ? " *" : ""}`}
                  </InputLabel>

                  <Select
                    value={value || ""}
                    onChange={onChange}
                    required={required}
                    onBlur={onBlur}
                    label={label}
                    disabled={disabled}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflowY: "auto",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#c0c0c0",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            background: "#acaaaaf7",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& input": {
                        marginRight: "20px",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        color: "rgb(68, 68, 69) !important",
                        height: "10px",
                        border: "none",
                        outline: "none",
                      },
                      "& fieldset": {
                        borderRadius: "10px",
                        border: "2px solid #D8DEE6 !important",
                        color: "rgb(235, 235, 241) !important",
                      },
                      "&& .MuiInputBase-root": {
                        color: "#ccc !important",
                        marginLeft: "20px !important",
                      },
                      "& label": {
                        fontSize: size === "1.8px !important",
                        lineHeight: "1.6rem !important",
                        letterSpacing: "0.02rem !important",
                        // color: "#000 !important",
                      },
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <StyledTextField
                  label={
                    required ? (
                      <>
                        {label}
                        <span style={{ color: "red" }}> *</span>
                      </>
                    ) : (
                      label
                    )
                  }
                  // label={label}
                  id="outlined-basic"
                  variant="outlined"
                  style={style}
                  rows={maxRows}
                  multiline={multipleLine}
                  className={`form-input w-100 ${className} ${
                    hideCountButton ? "hide-count-button" : ""
                  } ${invertBorder ? "invert-border" : "normal-border"} ${
                    multipleLine ? "multiple-line" : ""
                  }`}
                  fullWidth={fullWidth}
                  size={size}
                  placeholder={placeholder}
                  type={
                    type === "password"
                      ? showPassword
                        ? "text"
                        : "password"
                      : type
                  }
                  inputRef={inputRef}
                  InputProps={{
                    endAdornment: type === "password" && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ marginRight: "1px", color: "#ccc" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    ...inputProps,
                    inputRef: ref,

                    startAdornment: type === "dateText" && (
                      <InputAdornment
                        position="start"
                        sx={{ marginLeft: "10px" }}
                      >
                        <IconButton edge="start" onClick={handleIconClick}>
                          <CalendarTodayIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    inputRef: inputRef,
                    ...inputProps,
                  }}
                  disabled={disabled}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value || defaultValue}
                  sx={{
                    backgroundColor: backgroundColor || "black",
                  }}
                  error={!!error}
                  readOnly={readOnly}
                  onClick={onClick}
                  onFocus={onFocus}
                  onCut={handleCut}
                  onCopy={handleCopy}
                  onPaste={handlePast}
                  autoComplete={autocomplete}
                  contentEditable={contentEditable}
                />
              )
            }
          />

          {helperText && (
            <FormHelperText
              className={`error-text ${errorTextClassName}`}
              error={!!error}
              sx={{ color: "black" }}
            >
              {helperText}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    );
  }
);
export default FormInput;
